"use client"

import { usePathname } from "@/platform/navigation"
import type { ReactNode } from "react"
import { findReportBySlug, getReportHref, reportCatalog, reportCategories } from "@/lib/report-catalog"
import { canAccessReportApi } from "@/lib/report-hub-access"
import { isReportVisible } from "@/lib/report-visibility"
import { useReportVisibility } from "@/hooks/use-report-visibility"
import { useAuth } from "@/lib/auth-context"
import { useCloseReport } from "@/hooks/use-close-report"
import { Button } from "@/components/ui/button"

function resolveReportApiFromPath(pathname: string): string | null {
  const filterPath = pathname.replace(/\/(view|screen)$/, "")
  for (const category of reportCategories) {
    for (const report of reportCatalog[category.id]) {
      if (report.href === filterPath) return report.api
      if (getReportHref(report) === filterPath) return report.api
    }
  }

  const viewPrefix = "/dashboard/reports/view/"
  if (filterPath.startsWith(viewPrefix)) {
    const slug = filterPath.slice(viewPrefix.length).split("/").filter(Boolean)
    const report = findReportBySlug(slug)
    return report?.api ?? null
  }

  return null
}

export default function ReportsLayout({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  const closeReport = useCloseReport()
  const { user } = useAuth()
  const { data: visibility, isLoading } = useReportVisibility()
  const api = resolveReportApiFromPath(pathname)

  if (api && !isLoading && (!isReportVisible(api, visibility) || !canAccessReportApi(api, user?.modules))) {
    return (
      <div className="mx-auto max-w-lg space-y-4 py-12 text-center">
        <h1 className="text-xl font-bold">Report not available</h1>
        <p className="text-sm text-muted-foreground">
          This report has been hidden for your organization. Contact your administrator if you need access.
        </p>
        <Button onClick={closeReport}>Back to Reports</Button>
      </div>
    )
  }

  return children
}
